/** €X for whole euros, €X.XX otherwise. Empty string for null/undefined. */
export function formatPrice(n: number | null | undefined): string {
  if (n == null) return '';
  return Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2)}`;
}

/** yyyy-MM-dd in local time. */
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

/* ------------------------------------------------------------------ */
/* Season + month browsing                                             */
/* ------------------------------------------------------------------ */

/**
 * The studio takes bookings up to 31 October. Availability is browsed a month
 * at a time because LinkTwist caps a single availability request at 31 days —
 * asking for the whole season in one call returns a 400, which is what used to
 * silently limit the wizard to the next month only.
 */
const SEASON_END_MONTH = 9; // 0-based: October
const SEASON_END_DAY = 31;

/** 'yyyy-MM' key of a slot's date-time or a yyyy-MM-dd day. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

/** 'yyyy-MM' key of a Date, in local time. */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "August 2026" */
export function formatMonthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(y, m - 1, 1));
}

/** Last bookable day of the season containing (or following) `today`. */
function seasonEnd(today: Date): Date {
  const end = new Date(today.getFullYear(), SEASON_END_MONTH, SEASON_END_DAY);
  // Past this year's cut-off, the next bookable season is the one after it.
  return end < today
    ? new Date(today.getFullYear() + 1, SEASON_END_MONTH, SEASON_END_DAY)
    : end;
}

/** Every month the guest may browse: this month through the end of the season. */
export function seasonMonths(today: Date): string[] {
  const end = seasonEnd(today);
  const months: string[] = [];
  const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    months.push(monthKey(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

/**
 * The `[from, to)` window to request for one month. Never asks for days in the
 * past, and never past the season's end — both keep the span inside LinkTwist's
 * 31-day ceiling.
 */
export function monthRange(
  key: string,
  today: Date,
): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const nextMonth = new Date(y, m, 1);
  const end = seasonEnd(today);
  const seasonCutoff = addDays(end, 1);

  const from = first < today ? today : first;
  const to = nextMonth > seasonCutoff ? seasonCutoff : nextMonth;
  return { from: ymd(from), to: ymd(to) };
}

/**
 * Read the literal calendar parts of a LinkTwist date-time. Values are always
 * the activity's wall-clock in Santorini (e.g. "2026-06-30 07:00:00+03:00" or
 * date-only "2026-06-30"). We parse the components literally — never via
 * `new Date(iso)` — so the displayed day/time never shifts with the visitor's
 * own timezone, and so the space-separated form parses in every browser.
 */
function parts(iso: string): { y: number; mo: number; d: number; hh: number; mm: number } | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], hh: m[4] ? +m[4] : 0, mm: m[5] ? +m[5] : 0 };
}

/** The calendar day (yyyy-MM-dd) of a slot, used to group times by date. */
export function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

/** "Tue 7 Jul" */
export function formatDateLabel(iso: string): string {
  const p = parts(iso);
  if (!p) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(p.y, p.mo - 1, p.d));
}

/** "Tuesday, 7 July 2026" */
export function formatDateLong(iso: string): string {
  const p = parts(iso);
  if (!p) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(p.y, p.mo - 1, p.d));
}

/** "14:30" when the slot carries a time, otherwise null. */
export function formatTimeLabel(iso: string): string | null {
  const p = parts(iso);
  if (!p || (p.hh === 0 && p.mm === 0)) return null;
  return `${String(p.hh).padStart(2, '0')}:${String(p.mm).padStart(2, '0')}`;
}
